import React, { useState } from 'react';
import { TrendingUp, Search, BarChart3, Target, Sparkles, Send, Megaphone, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import CursorGlow from '../../components/CursorGlow';
import ScrollProgress from '../../components/ScrollProgress';

const DigitalGrowth = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    toast({
      title: "Harika! 📈",
      description: "SEO ve reklam ekibimiz 24 saat içinde sizinle iletişime geçecek.",
    });
    setFormData({ name: '', email: '', phone: '', projectType: '', budget: '', message: '' });
  };

  const features = [
    {
      icon: Search,
      title: 'SEO Optimizasyonu',
      description: 'Arama motorlarında üst sıralara çıkmanız için teknik ve içerik SEO'
    },
    {
      icon: Megaphone,
      title: 'Google Ads Yönetimi',
      description: 'Arama, display ve YouTube reklamları ile hedef kitlenize ulaşın'
    },
    {
      icon: DollarSign,
      title: 'Meta Ads Yönetimi',
      description: 'Facebook ve Instagram reklamları ile satışlarınızı artırın'
    },
    {
      icon: BarChart3,
      title: 'Performans Analizi',
      description: 'Google Analytics ve özel dashboard ile detaylı performans takibi'
    },
    {
      icon: Target,
      title: 'İçerik Stratejisi',
      description: 'Hedef kitlenize ulaşan, dönüşüm odaklı içerik planlaması'
    },
    {
      icon: TrendingUp,
      title: 'Büyüme Optimizasyonu',
      description: 'A/B testleri ve CRO ile sürekli iyileştirme'
    }
  ];

  const adServices = [
    { platform: 'Google Ads', services: ['Arama Reklamları', 'Display Reklamları', 'YouTube Reklamları', 'Remarketing', 'Shopping Ads'] },
    { platform: 'Meta Ads', services: ['Facebook Reklamları', 'Instagram Reklamları', 'Messenger Ads', 'Lookalike Audiences', 'Retargeting'] }
  ];

  const process = [
    { step: '01', title: 'Analiz & Audit', desc: 'Mevcut durumunuz, rakipleriniz ve pazar analizi' },
    { step: '02', title: 'Strateji', desc: 'Özelleştirilmiş SEO ve reklam planı oluşturulur' },
    { step: '03', title: 'Uygulama', desc: 'Kampanyalar kurulur ve optimizasyon yapılır' },
    { step: '04', title: 'Raporlama', desc: 'Sonuçlar takip edilir ve ROI optimize edilir' }
  ];

  const technologies = ['Google Ads', 'Meta Business Suite', 'Google Search Console', 'Ahrefs', 'SEMrush', 'Google Analytics 4', 'Google Tag Manager', 'Hotjar'];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-orange-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-amber-500/20 rounded-full blur-3xl bottom-0 left-1/4 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-orange-400 animate-spin-slow" />
                <span className="text-orange-400 text-sm font-medium">SEO & Dijital Reklam</span>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                SEO, Google Ads &
                <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent"> Meta Ads </span>
                Yönetimi
              </h1>

              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                Organik trafik için SEO, anında sonuç için Google Ads ve Meta Ads. 
                Dijital pazarlama stratejinizi uçtan uca yönetiyoruz.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-lg hover:scale-105 transition-all duration-300"
                  data-testid="service-cta-button"
                >
                  Ücretsiz Reklam Analizi
                </Button>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop"
                alt="Digital Growth"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-6 shadow-xl">
                <TrendingUp className="w-8 h-8 text-white mb-2" />
                <div className="text-white font-bold text-2xl">340%</div>
                <div className="text-orange-100 text-sm">ROAS Artışı</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            Dijital Pazarlama <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">Hizmetlerimiz</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-orange-500/50 transition-all duration-500 hover:transform hover:translateY(-4px) hover:shadow-2xl hover:shadow-orange-500/20"
                style={{
                  animation: 'fade-in-up 0.8s ease-out both',
                  animationDelay: `${index * 0.15}s`
                }}
              >
                <div className="relative w-14 h-14 mb-4">
                  <div className="absolute inset-0 border-2 border-transparent border-t-orange-500/60 border-r-amber-500/40 rounded-full animate-spin" style={{ animationDuration: '3s' }}></div>
                  <div className="absolute inset-0 border-2 border-transparent border-t-amber-500/60 border-r-orange-500/40 rounded-full animate-spin" style={{ animationDuration: '4s', animationDirection: 'reverse', transform: 'scale(0.8)' }}></div>
                  
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center transition-transform duration-500 group-hover:rotate-12 group-hover:scale-110">
                    <feature.icon className="w-7 h-7 text-white transition-transform duration-500 group-hover:rotate-[-12deg]" />
                  </div>

                  <div className="absolute inset-0 bg-orange-500/50 rounded-xl blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-500"></div>
                </div>

                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors duration-300">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Ads & Meta Ads Detail */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            Reklam <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">Platformları</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {adServices.map((platform, index) => (
              <div 
                key={index}
                className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 hover:border-orange-500/50 transition-all"
              >
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${index === 0 ? 'bg-gradient-to-br from-blue-500 to-green-500' : 'bg-gradient-to-br from-blue-600 to-purple-600'}`}>
                    <Megaphone className="w-6 h-6 text-white" />
                  </div>
                  {platform.platform}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {platform.services.map((service, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-300">
                      <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                      {service}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">Çalışma Süreci</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-orange-500/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-orange-500/50 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            Kullandığımız <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">Araçlar</span>
          </h2>

          <div className="flex flex-wrap justify-center gap-4">
            {technologies.map((tech, index) => (
              <div key={index} className="px-6 py-3 bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl text-gray-300 hover:border-orange-500/50 hover:text-orange-400 hover:scale-110 transition-all duration-300">
                {tech}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Form */}
      <section id="contact-form" className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[800px] h-[800px] bg-orange-500/10 rounded-full blur-3xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-slate-800/60 backdrop-blur-xl border border-orange-500/30 rounded-3xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Organik Büyümenizi
                <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent"> Hızlandıralım! </span>
              </h2>
              <p className="text-gray-400 text-lg">
                Ücretsiz SEO analizi ve büyüme danışmanlığı için formu doldurun
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-gray-300 mb-2 block">Adınız Soyadınız *</Label>
                  <Input
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-slate-900/50 border-orange-500/30 text-white h-12 rounded-xl focus:border-orange-500 focus:ring-orange-500 transition-all"
                    placeholder="Ahmet Yılmaz"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-300 mb-2 block">E-posta *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-slate-900/50 border-orange-500/30 text-white h-12 rounded-xl focus:border-orange-500 focus:ring-orange-500 transition-all"
                    placeholder="ahmet@sirket.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" className="text-gray-300 mb-2 block">Web Siteniz</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-slate-900/50 border-orange-500/30 text-white h-12 rounded-xl focus:border-orange-500 focus:ring-orange-500 transition-all"
                    placeholder="www.sirketiniz.com"
                  />
                </div>
                <div>
                  <Label htmlFor="budget" className="text-gray-300 mb-2 block">Aylık Bütçe</Label>
                  <select
                    id="budget"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    className="w-full bg-slate-900/50 border border-orange-500/30 text-white h-12 rounded-xl px-4 focus:border-orange-500 focus:ring-orange-500 transition-all"
                  >
                    <option value="">Seçiniz</option>
                    <option value="2k-5k">€2K - €5K / Ay</option>
                    <option value="5k-10k">€5K - €10K / Ay</option>
                    <option value="10k+">€10K+ / Ay</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-gray-300 mb-2 block">Hedefleriniz *</Label>
                <Textarea
                  id="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-slate-900/50 border-orange-500/30 text-white rounded-xl resize-none focus:border-orange-500 focus:ring-orange-500 transition-all"
                  placeholder="SEO ve dijital büyüme hedefleriniz neler? Hangi anahtar kelimelerde sıralama almak istiyorsunuz?"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white py-6 text-lg font-semibold rounded-xl group hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-orange-500/50"
              >
                Ücretsiz SEO Raporu Al
                <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>

              <p className="text-center text-sm text-gray-500">
                📈 Veri Odaklı • 💎 Şeffaf Raporlama • 🚀 Sonuç Garantili
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DigitalGrowth;
